<template>
  <div class="table-responsive">
    <template v-if="props.rows.length">
      <table class="table">
        <thead :class="props.headClass">
          <template v-if="!isMobile || !$slots.mobileColumns">
            <slot name="columns"/>
          </template>
          <template v-if="isMobile && $slots.mobileColumns">
            <slot name="mobileColumns"/>
          </template>
        </thead>

        <tbody>
          <template v-if="props.grouped">
            <template v-for="group in props.rows" :key="`_${group[props.groupKeyField]}-group`">
              <template v-if="!props.hideGroups">
                <slot name="group" :col-count="$slots.columns().length" :group="group"/>
              </template>

              <template v-for="(item, index) in group.items" :key="`_${item[props.keyField]}-row`">
                <slot
                  name="row"
                  :row="item"
                  :group="group"
                  :expand="expand"
                  :index="index"
                  :is-expanded="checkIfRowExpanded(item)"
                  :is-loading="checkIfRowLoading(item)"
                  :is-mobile="isMobile"
                />
                <row-collapse :hide-leave-duration="!!expandId">
                  <template v-if="props.details && checkIfRowExpanded(item)">
                    <tr class="table-details-row" :key="`_${item[props.keyField]}-details`">
                      <td class="p-0 border-0" :colspan="$slots.columns().length">
                        <div class="table-details">
                          <slot
                            name="details"
                            :row="item"
                            :data="expandData"
                            :group="group"
                          />
                        </div>
                      </td>
                    </tr>
                  </template>
                </row-collapse>
              </template>
            </template>
          </template>
          <template v-else>
            <template v-for="(item, index) in props.rows" :key="`_${item[props.keyField]}-row`">
              <slot
                name="row"
                :row="item"
                :expand="expand"
                :index="index"
                :is-expanded="checkIfRowExpanded(item)"
                :is-loading="checkIfRowLoading(item)"
                :is-mobile="isMobile"
              />
              <row-collapse :hide-leave-duration="!!expandId">
                <template v-if="props.details && checkIfRowExpanded(item)">
                  <tr class="table-details-row" :key="`_${item[props.keyField]}-details`">
                    <td class="p-0 border-0" :colspan="$slots.columns().length">
                      <div class="table-details">
                        <slot
                          name="details"
                          :row="item"
                          :data="expandData"
                        />
                      </div>
                    </td>
                  </tr>
                </template>
              </row-collapse>
            </template>
          </template>
        </tbody>
      </table>
    </template>

    <template v-else>
      <slot name="empty"/>
    </template>
  </div>
</template>


<script setup>
  import RowCollapse from '@/components/transitions/row-collapse';
  import { reportService, toastService } from '@/core';
  import { addEventListener, removeEventListener } from '@/utils/browser';
  import debounce from 'lodash/debounce';
  import { onBeforeUnmount, ref, reactive, onMounted, computed } from 'vue';

  const emit = defineEmits(['collapsed', 'expanded']);

  const props = defineProps({
    rows: {
      type: Array,
      default() {
        return [];
      },
    },
    grouped: {
      type: Boolean,
      default: false,
    },
    details: {
      type: Function,
      default: null,
    },
    fetchDetails: {
      type: Boolean,
      default: true,
    },
    keyField: {
      type: String,
      default: 'id',
    },
    groupKeyField: {
      type: String,
      default: 'id',
    },
    hideGroups: {
      type: Boolean,
      default: false,
    },
    headClass: {
      type: String,
      default: '',
    },
    expandOnMobile: {
      type: Boolean,
      default: false,
    },
  });

  const expandId = ref(null);
  const expandData = reactive({});
  const expandLoading = ref(false);
  const windowWidth = ref(null);

  const observeWindow = debounce(() => {
    windowWidth.value = window.innerWidth;
  }, 150);

  windowWidth.value = window.innerWidth;

  const isMobile = computed(() => windowWidth.value <= 1024);

  onMounted(() => {
    addEventListener(window, 'resize', observeWindow);
  });

  onBeforeUnmount(() => {
    removeEventListener(window, 'resize', observeWindow);
  });

  async function expand(row, group = undefined) {
    if (!props.expandOnMobile && isMobile.value) {
      return;
    }

    if (expandLoading.value) {
      return;
    }

    if (expandId.value === row[props.keyField]) {
      expandId.value = null;
      Object.assign(expandData, {});
      expandLoading.value = false;

      emit('collapsed');
      return;
    }

    expandLoading.value = row[props.keyField];
    try {
      // if false don't do any calls to api
      if (props.fetchDetails === false) {
        expandId.value = row[props.keyField];

        expandLoading.value = false;
      } else {
        await props.details(row, group).then(response => {
          if (response.data?.data) {
            expandId.value = row[props.keyField];
            Object.assign(expandData, response.data.data);

            emit('expanded', {
              id: expandId.value,
              data: expandData,
              row: row,
              group: group,
            });
          }

          expandLoading.value = false;
        });
      }
    } catch (e) {
      reportService.report(e);
      toastService.fromResponse(e);
    }
  }

  async function refresh(row, group = undefined) {
    if (!props.expandOnMobile && isMobile.value) {
      return;
    }

    if (expandLoading.value) {
      return;
    }

    expandLoading.value = row[props.keyField];
    try {
      await props.details(row, group).then(response => {
        expandId.value = row[props.keyField];
        if (response.data?.data) {
          Object.assign(expandData, response.data.data);
        }

        expandLoading.value = false;
      });
    } catch (e) {
      reportService.report(e);
      toastService.fromResponse(e);
    }
  }

  function checkIfRowExpanded(row) {
    if (!props.expandOnMobile && isMobile.value) {
      return false;
    }

    return Boolean(expandId.value === row[props.keyField] && expandData);
  }

  function checkIfRowLoading(row) {
    return expandLoading.value === row[props.keyField];
  }

  defineExpose({ expand, refresh, expandData });
</script>
